import React, { useState, useEffect } from 'react';
import { getStatusChipSx } from '../../../constants/statusColors';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  Tabs,
  Tab,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CorporateFare as ParentCompanyIcon,
  ChevronRightOutlined as ChevronRightIcon,
  AccountTree as OrgChartIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import CompanyHierarchyChart, { buildHierarchyData } from '../../../components/charts/CompanyHierarchyChart';
import { csAdminApi } from '../../../services/api';
import { mapCompanyFromApi, formatCompanyTypeLabel } from '../../../utils/apiMappers';
import { formatShareholdingPercent } from '../../../utils/shareholdingPercent';

const ParentCompanyTab = ({ companyId, company }) => {
  const navigate = useNavigate();
  const [parentCompanies, setParentCompanies] = useState([]);
  const [viewMode, setViewMode] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    fetchParentCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, company?.parent_company_id, company?.parent_company_name]);

  const fetchParentCompanies = async () => {
    setLoading(true);
    setLoadError('');

    try {
      if (company?.parent_company_id) {
        const chain = [];
        const visited = new Set();
        let parentId = company.parent_company_id;

        while (parentId && !visited.has(Number(parentId))) {
          visited.add(Number(parentId));
          const res = await csAdminApi.getCompanyById(parentId);
          const parent = mapCompanyFromApi(res.data?.data);
          if (!parent) break;

          chain.push({
            id: parent.id,
            name: parent.name,
            tradeLicenseNumber: parent.tradeLicenseNumber || '—',
            type: formatCompanyTypeLabel(parent.type),
            shareholdingInCurrent: chain.length === 0
              ? (company.shareholdingPercentage ?? company.shareholding_percentage ?? '—')
              : (parent.shareholdingPercentage ?? parent.shareholding_percentage ?? '—'),
            status: parent.status,
            level: chain.length + 1,
          });
          parentId = parent.parent_company_id;
        }

        if (chain.length === 0 && company.parent_company_name) {
          chain.push({
            id: company.parent_company_id,
            name: company.parent_company_name,
            tradeLicenseNumber: '—',
            type: 'PARENT COMPANY',
            shareholdingInCurrent: company.shareholdingPercentage ?? company.shareholding_percentage ?? '—',
            status: 'ACTIVE',
            level: 1,
          });
        }

        setParentCompanies(chain);
        return;
      }

      setParentCompanies([]);
    } catch (error) {
      console.error('Error loading parent companies:', error);
      setLoadError('Failed to load parent company details');
      if (company?.parent_company_name) {
        setParentCompanies([{
          id: company.parent_company_id,
          name: company.parent_company_name,
          tradeLicenseNumber: '—',
          type: 'PARENT COMPANY',
          shareholdingInCurrent: company.shareholdingPercentage ?? company.shareholding_percentage ?? '—',
          status: 'ACTIVE',
          level: 1,
        }]);
      } else {
        setParentCompanies([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyClick = (parentId) => {
    if (parentId) navigate(`/companies/${parentId}`);
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (parentCompanies.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Parent Company</Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          {company?.parent_company_id
            ? (loadError || 'Parent company details could not be loaded.')
            : 'This company has no parent entities.'}
        </Alert>
      </Paper>
    );
  }

  const chartData = buildHierarchyData(
    company,
    [],
    parentCompanies,
    handleCompanyClick
  );

  return (
    <Paper sx={{ p: 3 }}>
      {loadError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {loadError}. Showing limited parent details.
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Ownership Chain</Typography>
        <Chip
          label={`${parentCompanies.length} Parent ${parentCompanies.length === 1 ? 'Company' : 'Companies'}`}
          size="small"
          color="primary"
          variant="outlined"
        />
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={viewMode}
          onChange={(e, newValue) => setViewMode(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="List View" />
          <Tab label="Org Chart" icon={<OrgChartIcon fontSize="small" />} iconPosition="start" />
        </Tabs>
      </Box>

      {viewMode === 0 && (
        <List sx={{ p: 0 }}>
          {[...parentCompanies].reverse().map((parent, index) => (
            <React.Fragment key={parent.id}>
              <ListItem
                sx={{
                  px: 0,
                  py: 2,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' },
                }}
                onClick={() => handleCompanyClick(parent.id)}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '8px',
                    bgcolor: parent.level === 2 ? '#fee2e2' : '#fecaca',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mr: 2,
                  }}
                >
                  <ParentCompanyIcon
                    sx={{
                      fontSize: 28,
                      color: parent.level === 2 ? '#7f1d1d' : '#b91c1c',
                    }}
                  />
                </Box>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {parent.name}
                      </Typography>
                      <Chip
                        label={parent.status}
                        sx={{
                          ...getStatusChipSx(parent.status),
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          height: 20,
                          borderRadius: '10px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                        size="small"
                      />
                      <Chip
                        label={parent.type}
                        sx={{
                          bgcolor: 'transparent',
                          color: parent.level === 2 ? '#7f1d1d' : '#b91c1c',
                          border: `1px solid ${parent.level === 2 ? '#7f1d1d' : '#b91c1c'}`,
                          fontWeight: 500,
                          fontSize: '0.7rem',
                          height: 20,
                          borderRadius: '10px',
                        }}
                        size="small"
                      />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                      <Typography variant="body2" color="textSecondary">
                        {parent.tradeLicenseNumber}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        • Owns {formatShareholdingPercent(parent.shareholdingInCurrent, { suffix: false })}% of {company?.name}
                      </Typography>
                    </Box>
                  }
                />
                <IconButton size="small">
                  <ChevronRightIcon />
                </IconButton>
              </ListItem>
              {index < parentCompanies.length - 1 && (
                <>
                  <Divider />
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 1,
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                  }}
                  >
                    ↓ owns shares in ↓
                  </Box>
                  <Divider />
                </>
              )}
            </React.Fragment>
          ))}

          <Divider />
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 1,
            color: 'text.secondary',
            fontSize: '0.75rem',
          }}
          >
            ↓ owns shares in ↓
          </Box>
          <Divider />
          <ListItem
            sx={{
              px: 2,
              py: 2,
              bgcolor: 'rgba(185, 28, 28, 0.08)',
              borderRadius: 2,
              mt: 1,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '8px',
                bgcolor: '#fecaca',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                mr: 2,
              }}
            >
              <ParentCompanyIcon sx={{ fontSize: 28, color: '#b91c1c' }} />
            </Box>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {company?.name}
                  </Typography>
                  <Chip
                    label="Current Company"
                    sx={{
                      bgcolor: '#b91c1c',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 20,
                      borderRadius: '10px',
                    }}
                    size="small"
                  />
                </Box>
              }
              secondary={
                <Typography variant="body2" color="textSecondary">
                  {company?.tradeLicenseNumber} • {company?.type}
                </Typography>
              }
            />
          </ListItem>
        </List>
      )}

      {viewMode === 1 && (
        <Box sx={{ mt: 2 }}>
          <CompanyHierarchyChart
            key={`parent-chart-${parentCompanies.length}-${company?.id}`}
            nodes={chartData.nodes}
            edges={chartData.edges}
            onNodeClick={handleCompanyClick}
            height={400}
            showControls
          />
        </Box>
      )}
    </Paper>
  );
};

export default ParentCompanyTab;
